import React, { useCallback, useRef } from "react";
import { useTranslation } from "../../useTranslation";

import {
  PolicyInput,
  Requirement,
  PolicyInputValidState,
  PolicyState
} from "@monorepo/components";

interface EmailInputProps {
  id: string;
  email: string;
  required?: Requirement;
  validateEmail: (email: string) => boolean;
  onEmailChanged?: (email: string, isValid: boolean) => void;
  wrappedProps?: any;
  onAutofill?: () => void;
  autoComplete?: string;
  disabled?: boolean;
}

const EmailInputControl = (props: EmailInputProps) => {
  const {
    id,
    email,
    required,
    onEmailChanged,
    wrappedProps,
    onAutofill,
    autoComplete,
    disabled
  } = props;
  const [t] = useTranslation("app");

  const handleEmailChange = (state: PolicyState) => {
    if (onEmailChanged) {
      onEmailChanged(state.input, state.validity === PolicyInputValidState);
    }
  };

  const validateEmailRef = useRef(props.validateEmail);
  const applyEmailPolicy = useCallback(
    (newEmail: string): string => {
      if (
        !validateEmailRef ||
        !validateEmailRef.current ||
        !validateEmailRef.current(newEmail)
      ) {
        return "invalid";
      }

      return PolicyInputValidState;
    },
    [validateEmailRef]
  );

  const getEmailErrorMessage = useCallback(
    (validity: string): string => {
      switch (validity) {
        case "invalid":
          return t("email.errorMessage");

        default:
          return "";
      }
    },
    [t]
  );

  return (
    <PolicyInput
      disabled={disabled}
      id={id}
      value={email}
      type="email"
      onChange={handleEmailChange}
      placeholder={t("email.placeholder")}
      applyPolicy={applyEmailPolicy}
      policyErrorMessage={getEmailErrorMessage}
      wrappedProps={wrappedProps}
      required={required}
      onAutofill={onAutofill}
      autoComplete={autoComplete}
    />
  );
};

const EmailInput: React.FunctionComponent<EmailInputProps> = React.memo(EmailInputControl);
export default EmailInput;
